import React, { useEffect, useRef, useState } from 'react';
import { useExamplesStream } from '../hooks/useExamplesStream';

const PHASES = [
  'Reading the word…',
  'Drafting example sentences…',
  'Polishing pinyin and tones…',
  'Reviewing for natural phrasing…',
];
const PHASE_INTERVAL_MS = 3500;

/**
 * AIExampleSuggester — inline panel under a meaning's example list.
 * Streams candidate examples from the AI and lets the teacher accept or
 * reject each one. Accepted examples are appended to the parent form via
 * `onAccept(example)`. Rejected ones are dropped from the local list.
 *
 * Pre-flight: the parent passes the current word context (hanzi, pinyin,
 * definition, partOfSpeech, hskLevel). Hanzi + definition are required;
 * the parent gates the open-button on those.
 */
function AIExampleSuggester({
  hanzi,
  pinyin,
  definition,
  partOfSpeech,
  hskLevel,
  onAccept,
  onClose,
}) {
  const { status, examples, thinking, error, submit, cancel } = useExamplesStream();
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [rejectedIdx, setRejectedIdx] = useState(new Set());

  // StrictMode-safe single submission. The hook itself aborts the in-flight
  // request on real unmount via its own cleanup effect, so we don't need to
  // call reset() here — that would race with React 18 dev double-mount and
  // waste an Ollama call.
  const didStartRef = useRef(false);
  useEffect(() => {
    if (didStartRef.current) return;
    didStartRef.current = true;
    submit({ hanzi, pinyin, definition, partOfSpeech, hskLevel, count: 3 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotate phase label while streaming.
  useEffect(() => {
    if (status !== 'streaming') return undefined;
    const id = setInterval(() => {
      setPhaseIdx((i) => Math.min(i + 1, PHASES.length - 1));
    }, PHASE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [status]);

  // First token arrives → skip the "Reading the word…" placeholder if still on it.
  useEffect(() => {
    if (thinking && thinking.length > 0) {
      setPhaseIdx((i) => (i === 0 ? 1 : i));
    }
  }, [thinking]);

  const handleAccept = (idx) => {
    onAccept(examples[idx]);
    setRejectedIdx((prev) => new Set([...prev, idx]));
  };
  const handleReject = (idx) => {
    setRejectedIdx((prev) => new Set([...prev, idx]));
  };

  const visibleExamples = examples
    .map((ex, idx) => ({ ex, idx }))
    .filter(({ idx }) => !rejectedIdx.has(idx));

  return (
    <div
      style={{
        marginTop: 12,
        padding: 16,
        background: 'var(--paper-2, #f7f4ed)',
        border: '1px solid var(--rule, #e3dfd2)',
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {status === 'streaming' && (
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--ink-2, #6e6a5e)',
                animation: 'aiPulse 1.4s ease-in-out infinite',
              }}
            />
          )}
          <strong style={{ fontSize: 13 }}>
            {status === 'streaming'
              ? 'AI suggesting examples…'
              : status === 'error'
                ? 'AI suggestion failed.'
                : 'AI suggestions'}
          </strong>
          {status === 'streaming' && (
            <span
              key={PHASES[phaseIdx]}
              className="ai-phase"
              style={{
                fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                fontSize: 11,
                color: 'var(--ink-3, #8b8779)',
                letterSpacing: '0.06em',
              }}
            >
              {PHASES[phaseIdx]}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => { cancel(); onClose(); }}
          style={{
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            color: 'var(--ink-3, #8b8779)',
            fontSize: 12,
          }}
        >
          Close
        </button>
      </div>

      {error && (
        <p style={{ color: 'var(--vermillion, #c0392b)', fontSize: 12 }}>{error}</p>
      )}

      {visibleExamples.length === 0 && status !== 'streaming' && !error && (
        <p style={{ fontSize: 12, color: 'var(--ink-3, #8b8779)' }}>
          No suggestions left — close the panel or try again.
        </p>
      )}

      {visibleExamples.map(({ ex, idx }) => (
        <div
          key={idx}
          style={{
            background: 'white',
            border: '1px solid var(--rule, #e3dfd2)',
            borderRadius: 6,
            padding: 12,
            marginBottom: 8,
          }}
        >
          <div style={{ fontSize: 16, marginBottom: 4 }}>{ex.cn}</div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink-2, #6e6a5e)',
              fontStyle: 'italic',
              marginBottom: 4,
            }}
          >
            {ex.pinyin}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2, #6e6a5e)', marginBottom: 8 }}>
            {ex.en}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => handleReject(idx)}
              style={{
                fontSize: 11,
                padding: '4px 10px',
                background: 'transparent',
                border: '1px solid var(--rule, #e3dfd2)',
                borderRadius: 4,
                cursor: 'pointer',
                color: 'var(--ink-3, #8b8779)',
              }}
            >
              Reject
            </button>
            <button
              type="button"
              onClick={() => handleAccept(idx)}
              style={{
                fontSize: 11,
                padding: '4px 10px',
                background: 'var(--ink, #2a2722)',
                border: 0,
                borderRadius: 4,
                cursor: 'pointer',
                color: 'white',
              }}
            >
              Accept ↓
            </button>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes aiPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.9); }
          50%      { opacity: 1;   transform: scale(1.15); }
        }
        .ai-phase {
          animation: aiPhaseFade 500ms ease-out both;
        }
        @keyframes aiPhaseFade {
          from { opacity: 0; transform: translateY(2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default AIExampleSuggester;
